import {
  CONTINGENCY_CHANNEL_LABELS,
  CONTINGENCY_SCOPE_LABELS,
  LIA_STATE_LABELS,
  OPS_ALERT_STATUS_LABELS,
  OPS_INCIDENT_KIND_LABELS,
  OPS_SEVERITY_LABELS,
  RESUME_CATEGORIES,
  RESUME_CATEGORY_LABELS,
  RESUME_CHOICE_LABELS,
  type ContingencyStop,
  type LiaState,
  type OpsIncident,
  type OpsSeverity,
  type ResumeCategory,
  type ResumeChoice,
  type ResumePreview,
} from '@lia/contracts';
import { cn } from '../../lib/cn.js';
import { StatusBadge, type StatusTone } from '../primitives/badge.js';
import { Button } from '../primitives/button.js';
import { FormField } from '../primitives/form-field.js';
import { Select } from '../primitives/select.js';

// Contingency + alerts (setter spec §10.8, §10.9): LIA state, active AI stops, the resume reconciliation summary and
// the operational incident / alert log. Presentational only; screens wire the API.

export const LIA_STATE_TONE: Record<LiaState, StatusTone> = { operando: 'success', degradada: 'warning', pausada: 'danger' };
export const OPS_SEVERITY_TONE: Record<OpsSeverity, StatusTone> = { critical: 'danger', major: 'warning', minor: 'neutral' };
const DELIVERY_TONE: Record<keyof typeof OPS_ALERT_STATUS_LABELS, StatusTone> = { pending: 'info', sent: 'success', failed: 'danger', skipped: 'neutral' };

const fmt = (iso: string | null) => (iso ? new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso)) : '—');

export function LiaStateBadge({ state, className }: { state: LiaState; className?: string }) {
  return (
    <StatusBadge tone={LIA_STATE_TONE[state]} className={className}>
      LIA: {LIA_STATE_LABELS[state]}
    </StatusBadge>
  );
}

export function contingencyScopeText(s: Pick<ContingencyStop, 'scope' | 'projectName' | 'channel'>): string {
  const where = s.scope === 'project' ? `Proyecto ${s.projectName ?? ''}`.trim() : CONTINGENCY_SCOPE_LABELS[s.scope];
  return `${where} · ${CONTINGENCY_CHANNEL_LABELS[s.channel ?? 'all']}`;
}

export interface ContingencyStopListProps {
  stops: ContingencyStop[];
  onResume?: (stop: ContingencyStop) => void;
  emptyText?: string;
  className?: string;
}

/** Active (or historical) AI stops with scope, reason, who/when and the resume action when allowed. */
export function ContingencyStopList({ stops, onResume, emptyText = 'La IA está operando: no hay paros activos.', className }: ContingencyStopListProps) {
  if (!stops.length) return <p className={cn('text-body text-text-secondary', className)}>{emptyText}</p>;
  return (
    <ul className={cn('grid gap-2', className)} aria-label="Paros de contingencia">
      {stops.map((s) => (
        <li key={s.id} className="grid gap-1 rounded-card border border-border-subtle bg-surface-subtle p-3">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge tone={s.active ? 'danger' : 'neutral'}>{s.active ? 'IA detenida' : 'Reanudada'}</StatusBadge>
            <span className="text-body font-medium text-text-primary">{contingencyScopeText(s)}</span>
          </div>
          <p className="text-body text-text-primary">Motivo: {s.reason}</p>
          <p className="text-meta text-text-secondary">
            Activado {fmt(s.activatedAt)}
            {s.activatedBy ? ` por ${s.activatedBy}` : ''}
            {s.resumedAt ? ` · Reanudado ${fmt(s.resumedAt)}${s.resumedBy ? ` por ${s.resumedBy}` : ''}: ${s.resumeReason ?? ''}` : ''}
          </p>
          {s.active && onResume ? (
            s.canResume ? (
              <div>
                <Button type="button" size="sm" variant="secondary" onClick={() => onResume(s)}>
                  Revisar y reanudar
                </Button>
              </div>
            ) : (
              <p className="text-meta text-text-secondary">{s.scope === 'global' ? 'Solo Estateflow puede reanudar un paro global.' : 'Solo un administrador puede reanudar.'}</p>
            )
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export interface ResumeReconciliationProps {
  preview: ResumePreview;
  choices: Record<ResumeCategory, ResumeChoice>;
  onChoiceChange: (category: ResumeCategory, choice: ResumeChoice) => void;
  disabled?: boolean;
}

/** What is pending per category and what to do with it; stale and human-owned work is never run (UAT-47). */
export function ResumeReconciliation({ preview, choices, onChoiceChange, disabled }: ResumeReconciliationProps) {
  return (
    <div className="grid gap-3">
      {RESUME_CATEGORIES.map((c) => {
        const k = preview.categories[c];
        const id = `resume-choice-${c}`;
        return (
          <div key={c} className="grid gap-2 rounded-card border border-border-subtle p-3">
            <p className="text-body font-medium text-text-primary">
              {RESUME_CATEGORY_LABELS[c]}: {k.total}
            </p>
            <p className="text-meta text-text-secondary">
              {k.runnable} vigentes · {k.stale} vencidos (se omiten) · {k.humanOwned} con atención humana (LIA no los retoma)
            </p>
            {k.runnable > 0 ? (
              <FormField id={id} label="Qué hacer con los vigentes">
                <Select id={id} value={choices[c]} disabled={disabled} onValueChange={(v) => onChoiceChange(c, v as ResumeChoice)} options={Object.entries(RESUME_CHOICE_LABELS).map(([value, label]) => ({ value, label }))} />
              </FormField>
            ) : null}
          </div>
        );
      })}
      {preview.pausedCampaigns > 0 ? <p className="text-meta text-text-secondary">{preview.pausedCampaigns} campaña(s) quedaron en pausa: se reanudan manualmente desde Campañas.</p> : null}
      {preview.otherActiveStops > 0 ? <p className="text-meta text-text-secondary">Hay {preview.otherActiveStops} paro(s) más activos: lo que cubren sigue suspendido.</p> : null}
    </div>
  );
}

export interface OpsIncidentListProps {
  incidents: OpsIncident[];
  onResolve?: (incident: OpsIncident) => void;
  className?: string;
}

/** Operational incidents with severity, LIA state, impact, containment, owner and every alert sent (§10.9). */
export function OpsIncidentList({ incidents, onResolve, className }: OpsIncidentListProps) {
  if (!incidents.length) return <p className={cn('text-body text-text-secondary', className)}>Sin incidentes registrados.</p>;
  return (
    <ul className={cn('grid gap-2', className)} aria-label="Incidentes operativos">
      {incidents.map((i) => (
        <li key={i.id} className="grid gap-1 rounded-card border border-border-subtle p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-meta font-medium text-text-secondary">{i.code}</span>
            <StatusBadge tone={OPS_SEVERITY_TONE[i.severity]}>{OPS_SEVERITY_LABELS[i.severity]}</StatusBadge>
            <StatusBadge tone={i.status === 'open' ? 'warning' : 'success'}>{i.status === 'open' ? 'Abierto' : 'Resuelto'}</StatusBadge>
            <LiaStateBadge state={i.liaState} />
          </div>
          <p className="text-body font-medium text-text-primary">{i.title}</p>
          <p className="text-meta text-text-secondary">
            {OPS_INCIDENT_KIND_LABELS[i.kind]}
            {i.projectName ? ` · ${i.projectName}` : ''} · {i.occurrences} evento(s) · último {fmt(i.lastSeenAt)}
          </p>
          <p className="text-body text-text-primary">Impacto: {i.impact}</p>
          {i.containment ? <p className="text-meta text-text-secondary">Contención: {i.containment}</p> : null}
          {i.owner ? <p className="text-meta text-text-secondary">Seguimiento: {i.owner}</p> : null}
          {i.resolution ? (
            <p className="text-meta text-text-secondary">
              Resolución ({fmt(i.resolvedAt)}): {i.resolution}
              {i.pendingAfter ? ` · Pendiente: ${i.pendingAfter}` : ''}
            </p>
          ) : null}
          {i.deliveries.length ? (
            <ul className="grid gap-1" aria-label={`Alertas de ${i.code}`}>
              {i.deliveries.map((d) => (
                <li key={d.id} className="flex flex-wrap items-center gap-2 text-meta text-text-secondary">
                  <StatusBadge tone={DELIVERY_TONE[d.status]}>{OPS_ALERT_STATUS_LABELS[d.status]}</StatusBadge>
                  <span>
                    {d.channel === 'slack' ? 'Slack' : 'WhatsApp'} · {d.targetLabel} · {d.kind === 'recovery' ? 'recuperación' : d.kind === 'reminder' ? 'recordatorio' : 'aviso'}
                    {d.detail ? ` — ${d.detail}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-meta text-text-secondary">{i.severity === 'minor' ? 'Menor: se agrupa en el resumen periódico.' : 'Sin alertas enviadas (configura Slack y el responsable designado).'}</p>
          )}
          {i.status === 'open' && i.kind !== 'kill_switch' && onResolve ? (
            <div>
              <Button type="button" size="sm" variant="secondary" onClick={() => onResolve(i)}>
                Marcar resuelto
              </Button>
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
